/**
 * Country List Component - Display list of countries with pagination
 */

import React from 'react';
import { Loader2, Globe, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@voilajsx/uikit/button';
import { CountryCard } from './CountryCard';
import type { Country } from '../types';

interface CountryListProps {
  countries: Country[];
  pagination: {
    page: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  isLoading: boolean;
  onPageChange: (page: number) => void;
  onCountryClick: (countryId: number) => void;
}

export const CountryList: React.FC<CountryListProps> = ({
  countries,
  pagination,
  isLoading,
  onPageChange,
  onCountryClick
}) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex items-center gap-3">
          <Loader2 className="h-6 w-6 animate-spin text-success" />
          <span className="text-foreground font-medium">Loading countries...</span>
        </div>
      </div>
    );
  }

  if (countries.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-6">
          <Globe className="h-10 w-10 text-muted-foreground" />
        </div>
        <h3 className="text-xl font-bold text-foreground mb-2">No countries found</h3>
        <p className="text-muted-foreground text-center max-w-md">
          Try adjusting your filters to see more results
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Results Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Countries</h2>
          <p className="text-muted-foreground">
            Showing {((pagination.page - 1) * 20) + 1}-{Math.min(pagination.page * 20, pagination.total)} of {pagination.total} countries
          </p>
        </div>
      </div>

      {/* Country Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {countries.map((country) => (
          <CountryCard
            key={country.id}
            country={country}
            onClick={() => onCountryClick(country.id)}
          />
        ))}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between pt-6 border-t border-border">
          <div className="text-sm text-muted-foreground">
            Page {pagination.page} of {pagination.totalPages}
          </div>

          <div className="flex items-center gap-2">
            <Button
              onClick={() => onPageChange(pagination.page - 1)}
              disabled={!pagination.hasPrev}
              variant="outline"
              size="sm"
              className="gap-2"
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>

            <Button
              onClick={() => onPageChange(pagination.page + 1)}
              disabled={!pagination.hasNext}
              variant="outline"
              size="sm"
              className="gap-2"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
